import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import cx from "clsx";
import { ConstructionListRows } from "~/components/common/constructions/list/ConstructionListRows";
import type { NskResponse } from "~/models/Api";
import type { Deal } from "~/models/Deal";
import type {
	DealConstruction,
	DealConstructionContext,
	DealConstructionEdit
} from "~/models/DealConstruction";
import type { DealConstructionItem } from "~/models/DealConstructionItem";
import type { DealEstimationHeader } from "~/models/DealEstimationHeader";
import Styles from "./ConstructionList.module.scss";

type Props = {
	companyType: "client" | "vendor" | "partner";
	constructionContext: DealConstructionContext;
	deal: Deal;
	dealConstructionItems: DealConstructionItem[];
	docType: "billing" | "estimation";
	handleDestroy?:
		| ((deleteConstruction: DealConstruction) => Promise<NskResponse>)
		| null;
	handleMutateConstructions?: (() => Promise<void>) | null;
	handleUpdate?:
		| ((
				submitValue: DealConstructionEdit,
				estimationId: number | null,
				historyId: number | null
		  ) => Promise<NskResponse>)
		| null;
	handleUpdateEnabled?:
		| ((construction: DealConstruction) => Promise<void>)
		| null;
	handleUpdateSeq?:
		| ((constructions: DealConstruction[]) => Promise<void>)
		| null;
	onToggleSelect?: ((id: number) => void) | null;
	onToggleSelectAll?: ((ids: number[]) => void) | null;
	readonly: boolean;
	requestHeader?: DealEstimationHeader | null;
	selectedIds?: readonly number[] | null;
	showEstimationConfirmedButton?: boolean;
	showHighlight?: boolean;
	target?: "owner" | "rentee" | null;
};
export function ConstructionList({
	readonly,
	deal,
	dealConstructionItems,
	constructionContext,
	companyType,
	docType,
	showHighlight = false,
	showEstimationConfirmedButton = false,
	requestHeader = null,
	handleMutateConstructions = null,
	handleUpdate = null,
	handleDestroy = null,
	handleUpdateSeq = null,
	handleUpdateEnabled = null,
	selectedIds = null,
	onToggleSelect = null,
	onToggleSelectAll = null,
	target = null
}: Props) {
	const allConstructionIds = constructionContext.dealConstructions.map(
		(dealConstruction) => dealConstruction.id
	);
	const isSelectable = selectedIds !== null;
	const isAllSelected =
		isSelectable &&
		allConstructionIds.length > 0 &&
		allConstructionIds.length === selectedIds.length;
	return (
		<div>
			<table className={Styles.List}>
				<thead>
					<tr className={cx(Styles.ListRow, Styles.Header)}>
						{isSelectable ? (
							<th className={Styles.Select}>
								<InputCheckBox
									checked={isAllSelected}
									onChange={(): void => onToggleSelectAll?.(allConstructionIds)}
								/>
							</th>
						) : null}
						<th className={Styles.OrderHandle}>
							{handleUpdateSeq !== null && <span>並替</span>}
						</th>
						{handleUpdateEnabled !== null && (
							<th className={Styles.SwitchEnable}>施工対象</th>
						)}
						{docType === "estimation" && (
							<th className={Styles.EstimationType}>区分</th>
						)}
						<th className={Styles.Estimation}>施工項目</th>
						<th className={Styles.UnitAmount}>単価 * 数量</th>
						<th className={Styles.ChargePer}>負担割合</th>
						{target !== null && (
							<th className={Styles.ForAmount}>
								{target === "rentee" && "入居者請求額"}
								{target === "owner" && "オーナー請求額"}
							</th>
						)}
						<th className={Styles.Amount}>価格</th>
						{!readonly && (
							<th className={Styles.Edit}>
								{handleUpdate !== null ||
									(handleDestroy !== null && <span>編集</span>)}
							</th>
						)}
					</tr>
				</thead>
				<ConstructionListRows
					companyType={companyType}
					constructionContext={constructionContext}
					deal={deal}
					dealConstructionItems={dealConstructionItems}
					docType={docType}
					handleDestroy={handleDestroy}
					handleMutateConstructions={handleMutateConstructions}
					handleUpdate={handleUpdate}
					handleUpdateEnabled={handleUpdateEnabled}
					handleUpdateSeq={handleUpdateSeq}
					onToggleSelect={onToggleSelect}
					readonly={readonly}
					requestHeader={requestHeader}
					selectedIds={selectedIds}
					showEstimationConfirmedButton={showEstimationConfirmedButton}
					showHighlight={showHighlight}
					target={target}
				/>
			</table>
		</div>
	);
}
